"use client";

import { Table } from "@tanstack/react-table";
import { X, Filter } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useFilterActions, useFilterState } from "../filter";
import { DataTableViewOptions } from "./data-table-view-options";
import { CAMPAIGN_TYPES, PROPAGATION_OVERALL_STATUS } from "@/utils/campaign-types";

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
}

export function DataTableToolbar<TData>({
  table,
}: DataTableToolbarProps<TData>) {
  const filters = useFilterState();
  const { updateFilters, resetFilters } = useFilterActions();
  const isFiltered = filters.campaignType || filters.overallStatus;

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 items-center space-x-2">
        <Select
          value={filters.campaignType || "ALL"}
          onValueChange={(value) =>
            updateFilters({
              campaignType: value === "ALL" ? null : value,
              page: "1",
            })
          }
        >
          <SelectTrigger className="w-[200px] h-8 border-dashed">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <SelectValue placeholder="Tipo de Campanha" />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos os Tipos</SelectItem>
            <SelectItem value={CAMPAIGN_TYPES.PRO013}>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                Leve X Pague Y (PRO013)
              </span>
            </SelectItem>
            <SelectItem value={CAMPAIGN_TYPES.PRO014}>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                Promoção Itens (PRO014)
              </span>
            </SelectItem>
            <SelectItem value={CAMPAIGN_TYPES.PRO017}>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                Combo Moderno (PRO017)
              </span>
            </SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.overallStatus || "ALL"}
          onValueChange={(value) =>
            updateFilters({
              overallStatus: value === "ALL" ? null : value,
              page: "1",
            })
          }
        >
          <SelectTrigger className="w-[180px] h-8 border-dashed">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <SelectValue placeholder="Status" />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos os Status</SelectItem>
            <SelectItem value={PROPAGATION_OVERALL_STATUS.PENDING}>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                Pendente
              </span>
            </SelectItem>
            <SelectItem value={PROPAGATION_OVERALL_STATUS.IN_PROGRESS}>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                Em Progresso
              </span>
            </SelectItem>
            <SelectItem value={PROPAGATION_OVERALL_STATUS.SUCCESS}>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                Sucesso
              </span>
            </SelectItem>
            <SelectItem value={PROPAGATION_OVERALL_STATUS.PARTIAL_FAILURE}>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                Parcial
              </span>
            </SelectItem>
            <SelectItem value={PROPAGATION_OVERALL_STATUS.FAILED}>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                Falha
              </span>
            </SelectItem>
          </SelectContent>
        </Select>

        {isFiltered && (
          <Button
            variant="ghost_disabled"
            onClick={resetFilters}
            className="h-8 px-2 lg:px-3"
          >
            Resetar
            <X />
          </Button>
        )}
      </div>
      <DataTableViewOptions table={table} />
    </div>
  );
}
